import React, { useEffect, useState, useCallback, useMemo } from "react";
import "../styles/SideBar.css";
import RecentChatUserList from "./RecentChatUserList";
import GroupedUserList from "./GroupedUserList";
import SideBarHeader from "./SideBarHeader";
import SearchInput from "./shared/SearchInput";
import { SearchFilter as searchFilter, PanelMode as panelMode } from "../constants";
import { Loader } from "../shared/ui";
import { getChannelsWithMessages, filterUsersWithLastMessage } from "../utils/chatUtils";
// Map user object into UI-friendly shape
import { mapUserData } from "../utils/mapUserData";
import { useUnifiedChannelSearch } from "../hooks/useUnifiedChannelSearch";
import { sortByLastMessageDateDesc } from "../utils/sortByLastMessageDate";
import { fetchUserById } from "../chat-services/userService";
import { useChatStore } from "../store/useChatStore";
import { useNotificationActions } from "../hooks/useNotificationActions";
import NotificationConfirmationModal from "./Modals/NotificationConfirmationModal";

// Helper to move a user to the top of the list by id
const moveUserToTop = (users, userId) => {
  const restUsers = users.filter(u => u.id !== userId);
  const user = users.find(u => u.id === userId);
  return user ? [user, ...restUsers] : users;
};

const Sidebar = ({
                   chatClient,
                   onUserSelect,
                   currentUserId,
                   chatUserId,
                   mode,
                   setMode,
                   onChatDeleteClick,
                   setSidebarLoading,
                   onRecentUsersCountChange,
                   onBlockUserClick,
                 }) => {
  const [showGlobalRecentLoader, setShowGlobalRecentLoader] = useState(false);
  const [channelOffset, setChannelOffset] = useState(0);
  const [hasMoreChannels, setHasMoreChannels] = useState(true);
  const CHANNELS_PAGE_SIZE = searchFilter.LIMIT; // or any batch size you want
  const [deletedChannelIds, setDeletedChannelIds] = useState(new Set());
  const [recentSearchValue, setRecentSearchValue] = useState("");
  const [searchModeValue, setSearchModeValue] = useState("");
  const [offset, setOffset] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeUserId, setActiveUserId] = useState(chatUserId);
  const [isLoading, setIsLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);

  const setActiveChannel = useChatStore((s) => s.setActiveChannel);
  const activeChannel = useChatStore((s) => s.activeChannel);

  // Follow the selected user when it changes outside the sidebar (e.g. the My Schedule appointment
  // deep link selects one, deleting the open conversation clears it)
  useEffect(() => {
    setActiveUserId(chatUserId || null);
  }, [chatUserId]);

  // Use shared notification hook
  const {
    showNotificationModal,
    loading: notificationLoading,
    handleNotificationClick,
    handleModalClose,
    getConfirmHandler,
    getModalTitle,
    getModalMessage,
  } = useNotificationActions();

  const filterUsersFromActiveChannels = useCallback((excludeCids = []) => {
    const excludeSet = new Set([...deletedChannelIds, ...excludeCids]);
    const messageChannels = Object.values(chatClient.activeChannels).filter(
      (channel) =>
        channel.type === "messaging" &&
        !excludeSet.has(channel.cid) &&
        channel.data?.hidden !== true
    );

    const users = messageChannels.flatMap((channel) =>
      Object.values(channel.state.members)
        .filter(
          (member) =>
            member.user.id !== currentUserId
        )
        .map((member) => mapUserData(member.user, channel, chatClient))
    );
    const uniqueUsers = Array.from(new Map(users.map((u) => [u.id, u])).values());
    uniqueUsers.sort(sortByLastMessageDateDesc);
    setAllUsers(uniqueUsers);
    setFilteredUsers(uniqueUsers);
  }, [chatClient, currentUserId, deletedChannelIds]);

// Update fetchUsersFromChannels to accept offset/limit and return a promise
  const fetchUsersFromChannels = useCallback(async (offset = 0, append = false) => {
    const team = chatClient.user?.teams ? chatClient.user.teams[0] : "";
    const filters = {
      team: { $eq: team },
      type: "messaging",
      members: { $in: [currentUserId] },
      member_count: { $eq: 2 },
      last_message_at: { $exists: true },
    };
    const sort = [{ last_message_at: -1 }];
    const safeOffset = typeof offset === "number" && !isNaN(offset) ? offset : 0;
    const channels = await chatClient.queryChannels(filters, sort, {
      limit: CHANNELS_PAGE_SIZE,
      offset: safeOffset,
      watch: true,
      state: true,
    });
    // Filter out hidden channels in JS
    const visibleChannels = channels.filter(channel => !channel.data?.hidden);

    // Map users as before
    const users = visibleChannels.flatMap((channel) =>
      Object.values(channel.state.members)
        .filter(
          (member) =>
            member.user.id !== currentUserId
        )
        .map((member) => mapUserData(member.user, channel, chatClient))
    );

    const uniqueUsers = Array.from(
      new Map(users.map((u) => [u.id, u])).values()
    );
    uniqueUsers.sort(sortByLastMessageDateDesc);

    const mergeAndSortUsers = (prev, next) => {
      const map = new Map(prev.map((u) => [u.id, u]));
      next.forEach((u) => map.set(u.id, u));
      return Array.from(map.values()).sort(sortByLastMessageDateDesc);
    };

    if (append) {
      setAllUsers((prev) => mergeAndSortUsers(prev, uniqueUsers));
      setFilteredUsers((prev) => mergeAndSortUsers(prev, uniqueUsers));
    } else {
      // Move the active user to the top, even when we are launching from an attendee list
      const sorted = await ensureUserAtTopOfList(uniqueUsers.sort(sortByLastMessageDateDesc), chatUserId);
      setAllUsers(sorted);
      setFilteredUsers(sorted);
    }

    // Set whether there are more channels to load based on the number of visible channels with messages
    setHasMoreChannels(getChannelsWithMessages(visibleChannels)?.length === CHANNELS_PAGE_SIZE);
  }, [chatClient, currentUserId, deletedChannelIds]);

  // Prepend user to list if not present
  async function ensureUserAtTopOfList(list, userId) {
    if (!userId) return list;

    setActiveUserId(userId);
    onUserSelect(userId);
    // Check if a user already exists in the list
    if (list.some(user => user.id === userId)) {
      return moveUserToTop(list, userId);
    }

    // Fetch user from Stream Chat
    const user = await fetchUserById(chatClient, userId, mapUserData);
    if (!user) return list; // User not found

    // Prepend user to the list
    return [user, ...list];
  }

  const recentSearchOptions = useMemo(() => ({
    limit: searchFilter.LIMIT,
    offset: Number.isFinite(offset) ? offset : 0
  }), [offset, searchFilter.LIMIT]);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    loading
  } = useUnifiedChannelSearch(chatClient, recentSearchOptions);

  useEffect(() => {
    const loadRecent = async () => {
      await fetchUsersFromChannels(0, false);
    };

    if (!searchQuery.trim()) {
      loadRecent();
      return;
    }

    // Normalize searchResults if they are raw message results
    let normalizedResults = searchResults;
    if (
      searchResults.length > 0 &&
      searchResults[0].message &&
      !searchResults[0].matchType
    ) {
      normalizedResults = searchResults.map(result => ({
        channel: result.message.channel,
        matchType: "message",
        relevantMessages: [result.message],
      }));
    }

    if (normalizedResults && Array.isArray(normalizedResults)) {
      let users = [];
      normalizedResults.forEach(result => {
        if (result.matchType === "message" && Array.isArray(result.relevantMessages)) {
          result.relevantMessages.forEach(msg => {
            // Add the sender
            if (msg.user && msg.user.id !== currentUserId) {
              users.push(mapUserData(msg.user, result.channel, chatClient, msg));
            }
            // Add all channel members except current user, using the message for lastMessageText
            if (result.channel?.state?.members) {
              Object.values(result.channel.state.members).forEach(member => {
                if (member.user && member.user.id !== currentUserId) {
                  users.push(mapUserData(member.user, result.channel, chatClient, msg));
                }
              });
            }
          });
        } else if (result.channel?.state?.members) {
          Object.values(result.channel.state.members).forEach(member => {
            if (member.user && member.user.id !== currentUserId) {
              users.push(mapUserData(member.user, result.channel, chatClient));
            }
          });
        }
      });

      // Deduplicate by user id
      const uniqueUsers = Array.from(new Map(users.filter(Boolean).map(u => [u.id, u])).values());
      uniqueUsers.sort(sortByLastMessageDateDesc);

      // Efficiently update lastMessageText for uniqueUsers from allUsers
      const allUsersMap = new Map(allUsers.map(u => [u.id, u]));
      const mergedUsers = uniqueUsers.map(u => {
        const match = allUsersMap.get(u.id);
        return match ? {
          ...u,
          lastMessageText: match.lastMessageText,
          lastMessageDate: match.lastMessageDate
        } : u;
      });
      setFilteredUsers(mergedUsers);

      setHasMoreChannels(uniqueUsers.length === CHANNELS_PAGE_SIZE);
    }
  }, [searchQuery, searchResults, chatClient, currentUserId]);

  useEffect(() => {
    if (onRecentUsersCountChange) {
      onRecentUsersCountChange(filteredUsers.length > 0);
    }
  }, [filteredUsers, onRecentUsersCountChange]);

  // Wait for Stream Chat connection and fetch users
  useEffect(() => {
    let timeoutId;

    const handleConnected = async () => {
      timeoutId = setTimeout(async () => {
        try {
          await fetchUsersFromChannels(channelOffset, false);
        } catch (error) {
          // Log error with context for debugging and crash prevention
          console.error("Sidebar loading failed:", error);
        } finally {
          setIsLoading(false);
          if (typeof setSidebarLoading === "function") {
            setSidebarLoading(false);
          }
        }
      }, 300);
    };

    if (chatClient._hasConnectionID()) {
      handleConnected();
    } else {
      chatClient.on("connection.changed", () => {
        if (chatClient._hasConnectionID()) {
          handleConnected();
        }
      });
    }
    const handleChannelDeleted = (event) => {
      let parsedData;

      try {
        parsedData = JSON.parse(event.data); // Attempt to parse
      } catch (err) {
        console.warn("Invalid JSON in event.data:", err);
        return; // Stop execution if JSON is invalid
      }

      // Continue only if parsedData is valid
      const channelCid = parsedData?.channel?.cid;
      if (!channelCid) return;
      setDeletedChannelIds((prev) => new Set(prev).add(channelCid));
      filterUsersFromActiveChannels([channelCid]); // Exclude the deleted channel immediately
    };

    chatClient.on("channel.deleted", handleChannelDeleted);
    return () => {
      clearTimeout(timeoutId);
      chatClient.off("channel.deleted", handleChannelDeleted);
    };
  }, [chatClient, fetchUsersFromChannels]);

  // Listen for live events and update user state
  useEffect(() => {
    const updateUserFromChannel = (channel) => {
      const updatedUsers = Object.values(channel.state.members)
        .filter((m) => m.user.id !== currentUserId)
        .map((m) => mapUserData(m.user, channel, chatClient));

      setAllUsers((prev) => {
        const map = new Map(prev.map((u) => [u.id, u]));
        updatedUsers.forEach((u) => map.set(u.id, u));
        return Array.from(map.values());
      });

      setFilteredUsers((prev) => {

        // Merge updated users into prev, but keep the original order
        const updatedMap = new Map(updatedUsers.map((u) => [u.id, u]));
        const merged = prev.map((u) => updatedMap.get(u.id) || u);
        // Add any new users that weren't in prev
        updatedUsers.forEach((u) => {
          if (!prev.find((p) => p.id === u.id)) merged.push(u);
        });
        return merged;
      });
    };

    chatClient.on("channel.created.custom", (e) =>
      updateUserFromChannel(e.channel)
    );
    chatClient.on("message.new", async (e) => {
      const channel =
        chatClient.activeChannels[e.cid] ||
        chatClient.channel(e.channel_type, e.channel_id);
      await channel.watch({ watch: true, state: true, limit: 50 });
      console.log("Unread for", chatClient.user.id, "on", channel.cid, ":", channel.countUnread());
      updateUserFromChannel(channel, e.message);

      // Move the recipient to the top for a direct message not for thread messages
      if (e.message && e.message.user && e.message.user.id && e.message?.type === 'regular') {
        const channelMembers = Object.values(channel.state.members);

        // Collect all participant IDs except the current user
        const participantIds = channelMembers
          .map(m => m.user?.id)
          .filter(id => id && id !== currentUserId);

        // Remove duplicates and move each to the top
        const uniqueIds = Array.from(new Set(participantIds));
        setAllUsers(prev => uniqueIds.reduce((acc, id) => moveUserToTop(acc, id), prev));
        setFilteredUsers(prev => uniqueIds.reduce((acc, id) => moveUserToTop(acc, id), prev));
      }
    });

    chatClient.on("channel.updated", (e) => {
      const channel = chatClient.channel(e.channel_type, e.channel_id);
      updateUserFromChannel(channel, e.message);
    });
    chatClient.on("channel.hidden", (e) => {
      const channel = chatClient.channel(e.channel_type, e.channel_id);
      // Exclude this channel from the user list
      //fetchUsersFromChannels([channel.cid]);
      filterUsersFromActiveChannels([channel.cid])
    });
    // Add this handler for message deletion
    chatClient.on("message.deleted", async (e) => {
      const channel =
        chatClient.activeChannels[e.cid] ||
        chatClient.channel(e.channel_type, e.channel_id);
      await channel.watch({ watch: true, state: true, limit: 50 });
      updateUserFromChannel(channel);
    });

    return () => {
      chatClient.off("channel.created.custom");
      chatClient.off("message.new");
      chatClient.off("channel.updated");
      chatClient.off("channel.hidden");
      chatClient.off("message.deleted");
    };
  }, [chatClient, currentUserId]);

  const handleUserSelect = useCallback(
    async (userId) => {
      setSidebarLoading && setSidebarLoading(true);
      setActiveUserId(userId);
      onUserSelect(userId);
      //Find the channel for this user and mark as read
      const channel = Object.values(chatClient.activeChannels).find((channel) =>
        Object.values(channel.state.members).some((m) => m.user.id === userId)
      );
      if (channel) {
        await channel.markRead();
      }
      // TODO - Handle case where userId is not found in active channels
      // setSidebarLoading && setSidebarLoading(false);
    },
    [onUserSelect, allUsers, filteredUsers, chatClient]
  );

  const handleAutocompleteUserSelect = useCallback(
    async (user) => {
      setShowGlobalRecentLoader(true);
      const mapped = mapUserData(user, null, chatClient);

      setFilteredUsers((prev) => {
        // Remove any existing user with the same id
        const filtered = prev.filter((u) => u.id !== mapped.id);
        // Add the mapped user to the top
        return [mapped, ...filtered];
      });

      setActiveUserId(mapped.id);
      onUserSelect(mapped.id);
      setShowGlobalRecentLoader(false);
    },
    [allUsers, filteredUsers, chatClient, onUserSelect]
  );
  const handleLoadMoreChannels = async () => {
    const newOffset = channelOffset + CHANNELS_PAGE_SIZE;
    await fetchUsersFromChannels(newOffset, true);
    setChannelOffset(newOffset);
  };

  // Handler for edit icon in SearchInput
  const handleEdit = () => setMode(panelMode.SEARCH);

  const hasActiveChannelMessages = (channel) => {
    if (!channel) return false;
    const sets = channel.state?.messageSets;
    if (!Array.isArray(sets) || sets.length === 0) return false;
    const messages = sets[0]?.messages;
    return Array.isArray(messages) && messages.length > 0;
  };

  return (
    <>
      <div className="sidebar">
        <SideBarHeader
          mode={mode}
          onBackClick={() => {
            setMode(panelMode.RECENT);
            if (filteredUsers?.length) {
              setFilteredUsers(filterUsersWithLastMessage(filteredUsers));
              if (!hasActiveChannelMessages(activeChannel)) {
                setActiveChannel(false);
              }
            }
          }}
          onEditClick={() => setMode(panelMode.SEARCH)}
          onNotificationClick={handleNotificationClick}
        />
        {mode === panelMode.RECENT && (
          <SearchInput
            key="recent-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => {
              setSearchQuery("");
              setRecentSearchValue("");
            }}
            placeholder="Search Previous Chats"
            onEdit={handleEdit}
            loading={searchLoading}
          />
        )}
        {mode === panelMode.SEARCH && (
          <SearchInput
            key="search-mode"
            value={searchModeValue}
            placeholder="Search users"
            onEdit={handleEdit}
            onChange={(e) => setSearchModeValue(e.target.value)} // <-- Fix here
            onClear={() => setSearchModeValue("")}
            loading={searchLoading}
          />
        )}

        {isLoading || loading ? (
          <Loader loading={isLoading | loading} className="flex-center" />
        ) : (
          <div className="sidebar-recent-content">
            {mode === panelMode.SEARCH ? (
              <GroupedUserList
                chatClient={chatClient}
                currentUserId={currentUserId}
                searchValue={searchModeValue}
                onUserSelect={async (user) => {
                  setMode(panelMode.RECENT);
                  await handleAutocompleteUserSelect(user);
                  setSearchModeValue("");
                }}
                setLoading={setSearchLoading}
              />
            ) : (
              <RecentChatUserList
                searchValue={recentSearchValue}
                users={filteredUsers}
                activeUserId={activeUserId}
                onUserSelect={async (userId) => {
                  setShowGlobalRecentLoader(false);

                  setMode(panelMode.RECENT);
                  setRecentSearchValue("");
                  await handleUserSelect(userId);
                }}
                onOffset={(offset) => setOffset(offset)}
                onChatDeleteClick={onChatDeleteClick}
                loading={showGlobalRecentLoader}
                onLoadMore={handleLoadMoreChannels}
                hasMore={hasMoreChannels}
                onBlockUserClick={onBlockUserClick}
              />
            )}
          </div>
        )}
      </div>
      
      {/* Pause/Resume Notification Modal */}
      <NotificationConfirmationModal
        isOpen={showNotificationModal}
        toggle={handleModalClose}
        onConfirm={getConfirmHandler()}
        title={getModalTitle()}
        message={getModalMessage()}
        loading={notificationLoading}
      />
    </>
  );
};

export default Sidebar;
